import { openDB, DBSchema, IDBPDatabase } from 'idb';
import type { Product, Category } from '@/types/pos';
import type { EnginePromotion } from '@shared/pricing/promotion-engine';
import { useAuthStore } from '@/stores/auth.store';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Sale body queued for POST /sales (always carries the generated offlineId once queued). */
export type OfflineSalePayload = Record<string, unknown>;

/** Cart state captured at checkout so an offline receipt can be rendered later. */
export type OfflineCartSnapshot = Record<string, unknown>;

/** Location row cached for offline checkout (tax/FBR fields the checkout reads). */
export interface CachedLocation {
  id: string;
  name: string;
  type: string;
  isDefault: boolean;
  paymentMethodTaxEnabled: boolean;
  paymentMethodTaxRules: Record<string, { rate: number; name: string; overridesProductTax: boolean }> | null;
  atlEnabled: boolean;
  defaultBuyerAtlStatus: boolean;
  furtherTaxRate: number;
  fbrSroScheduleNo: string | null;
}

/** Bank account row cached for offline checkout (payment-method picker). */
export interface CachedBankAccount {
  id: string;
  name: string;
  bankName: string;
}

/** Tenant / FBR settings blobs are cached verbatim as returned by the API. */
export type CachedSettings = Record<string, unknown>;

export type OfflineSaleStatus = 'pending' | 'syncing' | 'synced' | 'failed' | 'discarded';

export interface OfflineSale {
  id: string;            // local UUID (also used as offlineId sent to backend)
  localInvoiceNumber: string; // e.g. OFF-20250519-0001
  payload: OfflineSalePayload; // the full sale payload we'd send to POST /sales (includes offlineId)
  cartSnapshot: OfflineCartSnapshot; // full cart state for offline receipt rendering
  createdAt: string;
  attemptCount: number;
  syncStatus: OfflineSaleStatus;
  syncError?: string;
  syncedSaleId?: string; // real sale ID after successful sync
  discardedAt?: string; // set when the owner removed a refused sale from the queue (POS-I65)
}

export interface OfflineCache {
  key: string;
  data: unknown;
  cachedAt: string;
}

export interface InvoiceSeq {
  key: string; // 'seq'
  value: number;
}

// ─── Schema ───────────────────────────────────────────────────────────────────

interface PosDB extends DBSchema {
  'offline-sales': {
    key: string;
    value: OfflineSale;
    indexes: { 'by-status': OfflineSaleStatus };
  };
  'offline-cache': {
    key: string;
    value: OfflineCache;
  };
  'offline-invoice-seq': {
    key: string;
    value: InvoiceSeq;
  };
}

// ─── DB Init ──────────────────────────────────────────────────────────────────

const DB_VERSION = 2;
/** Pre-POS-I109 single shared database (one per browser, not per store or user). */
export const LEGACY_OFFLINE_DB_NAME = 'clearring-offline';
let dbPromise: Promise<IDBPDatabase<PosDB>> | null = null;
let dbName: string | null = null;

/**
 * POS-I109: the offline store is one IndexedDB database per store + user, so queued sales and
 * cached products of one store can never be read, shown or synced under another store or user
 * signed in on the same browser.
 */
export function offlineDbNameFor(tenantId: string | null | undefined, userId: string | null | undefined): string {
  if (!tenantId || !userId) return `${LEGACY_OFFLINE_DB_NAME}:signed-out`;
  return `${LEGACY_OFFLINE_DB_NAME}:${tenantId}:${userId}`;
}

function currentDbName(): string {
  const { tenant, user } = useAuthStore.getState();
  return offlineDbNameFor(tenant?.id, user?.id);
}

function openPosDb(name: string) {
  return openDB<PosDB>(name, DB_VERSION, {
      upgrade(db, oldVersion) {
        // Version 1 — original
        if (oldVersion < 1) {
          db.createObjectStore('offline-sales', { keyPath: 'id' });
        }
        // Version 2 — extended
        if (oldVersion < 2) {
          if (!db.objectStoreNames.contains('offline-sales')) {
            const store = db.createObjectStore('offline-sales', { keyPath: 'id' });
            store.createIndex('by-status', 'syncStatus');
          } else {
            // The store exists from v1; add the missing index
            // Note: we can't add an index to an existing store during upgrade without recreation
            // We'll recreate it to add the index
            db.deleteObjectStore('offline-sales');
            const store = db.createObjectStore('offline-sales', { keyPath: 'id' });
            store.createIndex('by-status', 'syncStatus');
          }
          db.createObjectStore('offline-cache', { keyPath: 'key' });
          db.createObjectStore('offline-invoice-seq', { keyPath: 'key' });
        }
      },
    });
}

function getDb() {
  const name = currentDbName();
  if (!dbPromise || dbName !== name) {
    const previous = dbPromise;
    if (previous) previous.then((d) => d.close()).catch(() => undefined);
    dbName = name;
    dbPromise = openPosDb(name);
  }
  return dbPromise;
}

/** Close the open handle (the next call reopens the database of whoever is signed in then). */
export async function closeOfflineDb(): Promise<void> {
  const previous = dbPromise;
  dbPromise = null;
  dbName = null;
  if (previous) await previous.then((d) => d.close()).catch(() => undefined);
}

/**
 * Drop the cached catalogue/settings of one store + user (products, categories, locations,
 * bank accounts, settings, promotions, serials). Queued sales are kept: they belong to that
 * store and user and sync the next time that user signs in.
 */
export async function clearOfflineCacheFor(tenantId: string, userId: string): Promise<void> {
  const db = await openPosDb(offlineDbNameFor(tenantId, userId));
  try {
    await db.clear('offline-cache');
  } finally {
    db.close();
  }
}

/**
 * One-time move of the pre-POS-I109 shared database: its unsynced sales (pending, syncing,
 * failed) go into the database of the session that is signed in now — the same session that
 * would have synced them before this change — and the shared database is deleted, including
 * its cached catalogue. Returns the number of sales moved.
 */
export async function migrateLegacyOfflineDb(tenantId: string, userId: string): Promise<number> {
  if (typeof indexedDB === 'undefined') return 0;
  if (typeof indexedDB.databases === 'function') {
    const dbs = await indexedDB.databases();
    if (!dbs.some((d) => d.name === LEGACY_OFFLINE_DB_NAME)) return 0;
  }
  const legacy = await openPosDb(LEGACY_OFFLINE_DB_NAME);
  let moved = 0;
  try {
    const sales = (await legacy.getAll('offline-sales')).filter(
      (s) => s.syncStatus !== 'synced' && s.syncStatus !== 'discarded',
    );
    if (sales.length > 0) {
      const target = await openPosDb(offlineDbNameFor(tenantId, userId));
      try {
        for (const s of sales) {
          await target.put('offline-sales', s);
          moved++;
        }
        const seq = await legacy.get('offline-invoice-seq', 'seq');
        const targetSeq = await target.get('offline-invoice-seq', 'seq');
        if (seq && (!targetSeq || targetSeq.value < seq.value)) await target.put('offline-invoice-seq', seq);
      } finally {
        target.close();
      }
    }
  } finally {
    legacy.close();
  }
  await new Promise<void>((resolve) => {
    const req = indexedDB.deleteDatabase(LEGACY_OFFLINE_DB_NAME);
    req.onsuccess = req.onerror = req.onblocked = () => resolve();
  });
  return moved;
}

// ─── Offline Invoice Number ───────────────────────────────────────────────────

export async function generateOfflineInvoiceNumber(): Promise<string> {
  const db = await getDb();
  const tx = db.transaction('offline-invoice-seq', 'readwrite');
  const store = tx.objectStore('offline-invoice-seq');
  const record = await store.get('seq');
  const next = (record?.value ?? 0) + 1;
  await store.put({ key: 'seq', value: next });
  await tx.done;
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return `OFF-${yyyy}${mm}${dd}-${String(next).padStart(4, '0')}`;
}

// ─── Offline Sales Queue ──────────────────────────────────────────────────────

export async function queueOfflineSale(
  payload: OfflineSalePayload,
  cartSnapshot: OfflineCartSnapshot,
  localInvoiceNumber: string,
): Promise<string> {
  const db = await getDb();
  const id = crypto.randomUUID();
  const createdAt = new Date().toISOString();
  // POS-I66: the sale carries the time it was rung up and the OFF-… number the customer holds, so
  // the server books it on the day it happened and the paper receipt can be found afterwards.
  const saleWithId = { ...payload, offlineId: id, soldAt: createdAt, offlineReceiptNo: localInvoiceNumber };
  await db.put('offline-sales', {
    id,
    localInvoiceNumber,
    payload: saleWithId,
    cartSnapshot,
    createdAt,
    attemptCount: 0,
    syncStatus: 'pending',
  });
  return id;
}

export async function getQueuedSales(): Promise<OfflineSale[]> {
  const db = await getDb();
  const all = await db.getAll('offline-sales');
  // Return the sales still to be sent: pending, failed, and 'syncing' rows left behind by a page that
  // closed mid-sync (the engine runs one sync at a time, so a 'syncing' row found at the start of a
  // run is stale). Synced and discarded ones stay for receipt lookup.
  return all.filter((s) => s.syncStatus === 'pending' || s.syncStatus === 'syncing' || s.syncStatus === 'failed');
}

export async function getAllOfflineSales(): Promise<OfflineSale[]> {
  const db = await getDb();
  return db.getAll('offline-sales');
}

export async function getOfflineSale(id: string): Promise<OfflineSale | undefined> {
  const db = await getDb();
  return db.get('offline-sales', id);
}

export async function updateOfflineSaleStatus(
  id: string,
  status: OfflineSaleStatus,
  opts?: { syncedSaleId?: string; syncError?: string; attemptCount?: number },
): Promise<void> {
  const db = await getDb();
  const tx = db.transaction('offline-sales', 'readwrite');
  const existing = await tx.objectStore('offline-sales').get(id);
  if (!existing) { await tx.done; return; }
  await tx.objectStore('offline-sales').put({
    ...existing,
    syncStatus: status,
    syncedSaleId: opts?.syncedSaleId ?? existing.syncedSaleId,
    syncError: opts?.syncError ?? existing.syncError,
    attemptCount: opts?.attemptCount ?? existing.attemptCount,
  });
  await tx.done;
}

export async function removeQueuedSale(id: string): Promise<void> {
  const db = await getDb();
  await db.delete('offline-sales', id);
}

/** Sales still waiting to be sent (pending, or mid-sync). Refused ones are counted by getFailedSaleCount. */
export async function getPendingSaleCount(): Promise<number> {
  const db = await getDb();
  const all = await db.getAll('offline-sales');
  return all.filter((s) => s.syncStatus === 'pending' || s.syncStatus === 'syncing').length;
}

/** Sales the server refused (4xx) that still sit in the queue waiting for the owner (POS-I65). */
export async function getFailedOfflineSales(): Promise<OfflineSale[]> {
  const db = await getDb();
  const all = await db.getAll('offline-sales');
  return all
    .filter((s) => s.syncStatus === 'failed')
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function getFailedSaleCount(): Promise<number> {
  return (await getFailedOfflineSales()).length;
}

/**
 * Take a refused sale out of the queue. The row is kept (status 'discarded', with the time) so its
 * receipt number can still be looked up, but it is never sent again and no banner counts it.
 */
export async function discardOfflineSale(id: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction('offline-sales', 'readwrite');
  const existing = await tx.objectStore('offline-sales').get(id);
  if (existing && existing.syncStatus === 'failed') {
    await tx.objectStore('offline-sales').put({
      ...existing,
      syncStatus: 'discarded',
      discardedAt: new Date().toISOString(),
    });
  }
  await tx.done;
}

// Banner count: everything still in the queue that needs sending or attention
export async function getQueuedSaleCount(): Promise<number> {
  return (await getPendingSaleCount()) + (await getFailedSaleCount());
}

// ─── Offline Cache ────────────────────────────────────────────────────────────

const CACHE_KEYS = {
  PRODUCTS: 'products',
  CATEGORIES: 'categories',
  LOCATIONS: 'locations',
  BANK_ACCOUNTS: 'bank_accounts',
  TENANT_SETTINGS: 'tenant_settings',
  FBR_SETTINGS: 'fbr_settings',
  PROMOTIONS: 'promotions',
  SERIALS: 'available_serials',
} as const;

async function setCacheEntry(key: string, data: unknown): Promise<void> {
  const db = await getDb();
  await db.put('offline-cache', { key, data, cachedAt: new Date().toISOString() });
}

async function getCacheEntry<T>(key: string): Promise<T | null> {
  const db = await getDb();
  const entry = await db.get('offline-cache', key);
  return entry ? (entry.data as T) : null;
}

export const setOfflineCachedProducts = (data: Product[]) => setCacheEntry(CACHE_KEYS.PRODUCTS, data);
export const getOfflineCachedProducts = () => getCacheEntry<Product[]>(CACHE_KEYS.PRODUCTS);

export const setOfflineCachedCategories = (data: Category[]) => setCacheEntry(CACHE_KEYS.CATEGORIES, data);
export const getOfflineCachedCategories = () => getCacheEntry<Category[]>(CACHE_KEYS.CATEGORIES);

export const setOfflineCachedLocations = (data: CachedLocation[]) => setCacheEntry(CACHE_KEYS.LOCATIONS, data);
export const getOfflineCachedLocations = () => getCacheEntry<CachedLocation[]>(CACHE_KEYS.LOCATIONS);

export const setOfflineCachedBankAccounts = (data: CachedBankAccount[]) => setCacheEntry(CACHE_KEYS.BANK_ACCOUNTS, data);
export const getOfflineCachedBankAccounts = () => getCacheEntry<CachedBankAccount[]>(CACHE_KEYS.BANK_ACCOUNTS);

export const setOfflineCachedTenantSettings = (data: CachedSettings) => setCacheEntry(CACHE_KEYS.TENANT_SETTINGS, data);
export const getOfflineCachedTenantSettings = () => getCacheEntry<CachedSettings>(CACHE_KEYS.TENANT_SETTINGS);

export const setOfflineCachedFbrSettings = (data: CachedSettings) => setCacheEntry(CACHE_KEYS.FBR_SETTINGS, data);
export const getOfflineCachedFbrSettings = () => getCacheEntry<CachedSettings>(CACHE_KEYS.FBR_SETTINGS);

/** Automatic promotions in engine shape, so the till can price a cart offline (POS-I78). */
export const setOfflineCachedPromotions = (data: EnginePromotion[]) => setCacheEntry(CACHE_KEYS.PROMOTIONS, data);
export const getOfflineCachedPromotions = () => getCacheEntry<EnginePromotion[]>(CACHE_KEYS.PROMOTIONS);

/** An in-stock serial / IMEI of a serial-tracked product, cached so a phone can be sold offline (POS-I67). */
export interface CachedSerial {
  id: string;
  productId: string;
  serialNo: string;
  status: string;
}
export const setOfflineCachedSerials = (data: CachedSerial[]) => setCacheEntry(CACHE_KEYS.SERIALS, data);
export const getOfflineCachedSerials = () => getCacheEntry<CachedSerial[]>(CACHE_KEYS.SERIALS);

/** Serial numbers already used by sales still waiting in the offline queue (they must not be sold twice). */
export async function getQueuedSerialNumberIds(): Promise<string[]> {
  const db = await getDb();
  const all = await db.getAll('offline-sales');
  const ids: string[] = [];
  for (const sale of all) {
    if (sale.syncStatus === 'synced' || sale.syncStatus === 'discarded') continue;
    const items = (sale.payload as { items?: Array<{ serialNumberId?: string | null }> }).items ?? [];
    for (const item of items) if (item.serialNumberId) ids.push(item.serialNumberId);
  }
  return ids;
}
